import { apiClient } from './client'
import { ApiError } from './errors'

export interface WalletSummary {
  balance: number
  walletBalance: number
  expirableWalletCreditBalance: number
  withdrawableAmount: number
  withheldFunds: number
  rewardCredit: number
  voucherCredit: number
}

// [BUG-012] The feed serves Rails ShowActivity groups (dual ledger, one row
// per ActivityReference) in the GraphQL-era shape ActivityItem still renders.
export interface ActivityLedgerSource {
  sourceCategorisation: string
  sourceInfo: string
  sourceNote?: string | null
}

export interface ActivityLedgerEntry {
  createdAt: string
  credit: number
  debit: number
  source: ActivityLedgerSource
  __typename?: string
}

export interface ActivityRow {
  id: number
  createdAt: string
  reference: string
  total: string
  entries: ActivityLedgerEntry[]
  vault?: { id: number; name: string | null } | null
}

export interface WalletActivity {
  entries: ActivityRow[]
  totalCount: number
  pageInfo: { hasNextPage: boolean }
}

export interface PerformanceEntry {
  spotDate: string
  totalHoldingBalance: number
}

export async function fetchWallet(): Promise<WalletSummary> {
  try {
    const response = await apiClient.get<WalletSummary>('wallet')
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

// BE /wallet/activity row: one ActivityReference group, camelCase, with
// debit/credit as money strings.
interface WalletActivityItem {
  id: number
  reference: string
  createdAt: string
  total: string
  vault?: { id: number; name: string | null } | null
  entries: Array<{
    debit: string
    credit: string
    createdAt: string
    __typename?: string
    source: ActivityLedgerSource
  }>
}

interface WalletActivityResponse {
  items: WalletActivityItem[]
  total: number
  page: number
  page_size: number
}

export async function fetchWalletActivity(
  page: number,
  pageSize: number
): Promise<WalletActivity> {
  try {
    const response = await apiClient.get<WalletActivityResponse>(
      'wallet/activity',
      {
        params: { page, page_size: pageSize }
      }
    )
    const data = response.data
    const items = data?.items ?? []
    // Money strings → numbers for the renderer; everything else passes through.
    const entries: ActivityRow[] = items.map(item => ({
      id: item.id,
      reference: item.reference,
      createdAt: item.createdAt,
      total: item.total,
      vault: item.vault,
      entries: item.entries.map(entry => ({
        debit: Number(entry.debit ?? 0),
        credit: Number(entry.credit ?? 0),
        createdAt: entry.createdAt,
        __typename: entry.__typename,
        source: entry.source
      }))
    }))
    const total = data?.total ?? 0
    const currentPage = data?.page ?? page
    const perPage = data?.page_size ?? pageSize
    return {
      entries,
      totalCount: total,
      pageInfo: { hasNextPage: currentPage * perPage < total }
    }
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}

export async function fetchWalletPerformanceHistory(
  productIds?: number[],
  vaultIds?: number[],
  timeframe?: string
): Promise<PerformanceEntry[]> {
  try {
    const response = await apiClient.get<PerformanceEntry[]>(
      'wallet/performance_history',
      {
        params: {
          ...(productIds ? { product_ids: productIds } : {}),
          ...(vaultIds ? { vault_ids: vaultIds } : {}),
          ...(timeframe ? { timeframe } : {})
        }
      }
    )
    return response.data
  } catch (err) {
    throw ApiError.fromAxiosError(err)
  }
}
